'use strict';

/**
 * ═══════════════════════════════════════════════════════════
 * MODULE: DOM & FUNCTION UTILITIES
 * ES5-compatible utility helpers for class manipulation,
 * HTML escaping, and cross-browser clipboard copy actions.
 * ═══════════════════════════════════════════════════════════
 */

/**
 * Check if element has class
 * @param {HTMLElement} el
 * @param {string} className
 * @returns {boolean}
 */
export function hasClass(el, className) {
  if (!el || !className) return false;
  if (el.classList) {
    return el.classList.contains(className);
  }
  var current = el.className || '';
  return (' ' + current + ' ').indexOf(' ' + className + ' ') > -1;
}

/**
 * Add class(es) to element, supporting space-separated classes
 * @param {HTMLElement} el
 * @param {string} className
 */
export function addClass(el, className) {
  if (!el || !className) return;
  var classes = className.split(/\s+/);
  for (var i = 0; i < classes.length; i++) {
    var cls = classes[i];
    if (!cls) continue;
    if (el.classList) {
      el.classList.add(cls);
    } else {
      var current = el.className || '';
      var regex = new RegExp('(?:^|\\s)' + cls + '(?!\\S)', 'g');
      if (!current.match(regex)) {
        el.className = current + (current ? ' ' : '') + cls;
      }
    }
  }
}

/**
 * Remove class(es) from element, supporting space-separated classes
 * @param {HTMLElement} el
 * @param {string} className
 */
export function removeClass(el, className) {
  if (!el || !className) return;
  var classes = className.split(/\s+/);
  for (var i = 0; i < classes.length; i++) {
    var cls = classes[i];
    if (!cls) continue;
    if (el.classList) {
      el.classList.remove(cls);
    } else {
      var current = el.className || '';
      var regex = new RegExp('(?:^|\\s)' + cls + '(?!\\S)', 'g');
      el.className = current.replace(regex, ' ').replace(/^\s+|\s+$/g, '').replace(/\s+/g, ' ');
    }
  }
}

/**
 * Toggle class on element
 * @param {HTMLElement} el
 * @param {string} className
 * @param {boolean} [force]
 */
export function toggleClass(el, className, force) {
  if (!el || !className) return;
  var show = typeof force === 'boolean' ? force : !hasClass(el, className);
  if (show) {
    addClass(el, className);
  } else {
    removeClass(el, className);
  }
}

/**
 * Escape HTML characters to prevent XSS
 * @param {*} value
 * @returns {string}
 */
export function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Decode HTML entities back to plain text
 * @param {*} value
 * @returns {string}
 */
export function unescapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/**
 * Copy text to clipboard with legacy fallback
 * @param {string} text
 * @param {Function} [callback]
 */
export function copyToClipboard(text, callback) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text)
      .then(function () {
        if (callback) callback(true);
      })
      .catch(function () {
        copyToClipboardFallback(text, callback);
      });
  } else {
    copyToClipboardFallback(text, callback);
  }
}

/**
 * Fallback mechanism for older/insecure environments
 * @param {string} text
 * @param {Function} [callback]
 */
export function copyToClipboardFallback(text, callback) {
  var textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    var successful = document.execCommand('copy');
    if (callback) callback(successful);
  } catch (err) {
    if (callback) callback(false);
  }

  document.body.removeChild(textArea);
}

// Expose DOMUtils globally for legacy/inline scripts
var DOMUtils = {
  hasClass: hasClass,
  addClass: addClass,
  removeClass: removeClass,
  toggleClass: toggleClass,
  escapeHtml: escapeHtml,
  unescapeHtml: unescapeHtml,
  copyToClipboard: copyToClipboard,
  copyToClipboardFallback: copyToClipboardFallback
};

window.DOMUtils = DOMUtils;
// Maintain backwards compatibility with legacy window.Utils references
window.Utils = window.Utils || {};
for (var key in DOMUtils) {
  if (DOMUtils.hasOwnProperty(key)) {
    window.Utils[key] = DOMUtils[key];
  }
}
